/**
 * Sales phase targets — allocation amounts × phase sale months.
 * Kept separate so cash-event builders and sales schedules share one definition.
 *
 * Three distinct sale months require delivery − permit ≥ 2. Never silently
 * collide two phases into one month — callers must treat a short window as infeasible.
 */

import { n } from './math.js';
import { isThreeSaleWindowFeasible } from './timingControls.js';

/**
 * Build cumulative sales targets from allocation + phase sale months.
 * Offsets are months after permit; phases must be strictly increasing.
 * @returns {Array<{ month: number, cumPct: number, phase: string, phaseAmount: number }>|null}
 *   null when the programme cannot host three distinct sale months.
 */
export function buildSalesPhaseTargets(programme, allocation, timing) {
  const auth = Math.max(1, programme.permitMonth || programme.authorizationEnd || 1);
  const delivery = Math.max(auth, programme.deliveryMonth || programme.totalMonths);
  const maxOff = Math.max(0, delivery - auth);

  if (!isThreeSaleWindowFeasible({ permitMonth: auth, deliveryMonth: delivery, maxSaleOffset: maxOff })) {
    return null;
  }

  let preOff = Math.round(Number(timing.preBankEndOffsetFromAuthorization) || 0);
  let postOff = Math.round(Number(timing.postBankEndOffsetFromAuthorization) || 0);
  let lateOff = Math.round(Number(timing.lateEndOffsetFromAuthorization) || 0);

  // Strict sequence — never collapse two sales onto one month.
  preOff = Math.min(Math.max(0, preOff), maxOff - 2);
  postOff = Math.min(Math.max(preOff + 1, postOff), maxOff - 1);
  lateOff = Math.min(Math.max(postOff + 1, lateOff), maxOff);
  if (postOff <= preOff) postOff = Math.min(maxOff - 1, preOff + 1);
  if (lateOff <= postOff) lateOff = Math.min(maxOff, postOff + 1);

  const preEnd = Math.min(delivery, auth + preOff);
  const postEnd = Math.min(delivery, auth + postOff);
  const lateEnd = Math.min(delivery, auth + lateOff);

  // Final guard: refuse to return a colliding schedule.
  if (!(preEnd < postEnd && postEnd < lateEnd)) {
    return null;
  }

  const preAmt = Math.max(0, allocation.preBank);
  const postAmt = Math.max(0, allocation.postBank);
  const lateAmt = Math.max(0, allocation.late);
  const sum = preAmt + postAmt + lateAmt;
  const pre = sum > 0 ? preAmt / sum : 0;
  const post = sum > 0 ? postAmt / sum : 0;
  const late = sum > 0 ? lateAmt / sum : 0;

  return [
    { month: preEnd, cumPct: pre, phase: 'preBank', phaseAmount: pre },
    { month: postEnd, cumPct: pre + post, phase: 'postBank', phaseAmount: post },
    { month: lateEnd, cumPct: 1, phase: 'late', phaseAmount: late },
  ];
}

/** @deprecated Thin helper for call sites that only need cum %. */
export function salesPctAtMonth(month, programme, phaseTargetsOrMilestones) {
  const auth = Math.max(1, programme.permitMonth || programme.authorizationEnd || 1);
  let pct = 0;
  for (const m of phaseTargetsOrMilestones || []) {
    const hit = m.month != null
      ? m.month
      : auth + (Number(m.offsetFromAuthorization) || 0);
    const target = m.cumPct != null ? m.cumPct : m.pct;
    if (month >= hit) pct = Math.max(pct, n(target));
  }
  if (month >= (programme.deliveryMonth || programme.totalMonths)) pct = 1;
  return Math.min(1, Math.max(0, pct));
}
